import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { useMemo, useState } from 'react';
import clsx from 'clsx';
import { SegmentedControl, type TabItem } from '../../../components/ui/Tabs';
import { Card, CardHeader, InlineStat, StatTile } from '../../../components/ui/Card';
import { useHealthQuery, sectionLabel } from './queries';
import { UnavailableSources } from '../../Marketing/shared';
import { formatDayMonth, formatDayMonthYear } from '../../../utils/dateFormat';
import type { GtmHealthData } from '../../../api/gtm';
import { fmtNumber, isSparseColumn } from './components/format';
import {
  CHART_AXIS,
  CHART_GRID,
  CHART_HEIGHT,
  CHART_SERIES,
  CHART_TOOLTIP,
} from '../../../components/ui/chart';
import { Table, THead, Th, Td, Tr, Nil, TableFooter, isConstant } from '../../../components/ui/Table';
import { Pagination, usePagination } from '../../../components/ui/Pagination';
import { EmptyState, LoadingState, ErrorState } from '../../../components/ui/States';
import { Badge } from '../../../components/ui/Badge';

export function HealthView({
  startDate,
  endDate,
  periodLabel,
}: {
  startDate?: string;
  endDate?: string;
  /** "Last 90 days": named on every tile, beside its source. */
  periodLabel?: string;
}) {
  const query = useHealthQuery(startDate, endDate);
  const { data, isLoading, error } = query;
  // "Needs action" filters the coverage table to the pages it counts.
  const [coverageFilter, setCoverageFilter] = useState<CoverageException['kind'] | null>(null);
  const showCoverage = (kind: CoverageException['kind']) => {
    setCoverageFilter(kind);
    document.getElementById(INDEX_COVERAGE_ID)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (error) {
    return <ErrorState title="Couldn’t load Tech / SEO health" error={error} onRetry={() => query.refetch()} />;
  }
  if (isLoading) {
    return <LoadingState rows={4} />;
  }

  return (
    <div className="space-y-6">
      {data && <UnavailableSources sources={data.unavailable} label={sectionLabel} />}

      <Scorecards data={data} periodLabel={periodLabel} />
      <CoverageExceptions data={data} onSelect={showCoverage} />

      {/* Side by side only when both have something to show. With no Site Audit
          snapshot the card is left out and one quiet line says why. */}
      {data?.siteAudit ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <IndexCoverage data={data} filter={coverageFilter} onClearFilter={() => setCoverageFilter(null)} />
          <SiteAudit data={data} />
        </div>
      ) : (
        <>
          <IndexCoverage data={data} filter={coverageFilter} onClearFilter={() => setCoverageFilter(null)} />
          <p className="px-1 text-body-xs text-text-tertiary">
            Site audit hidden: there is no Semrush Site Audit snapshot, so crawl, redirect and hreflang checks can’t be
            shown. Set up a Site Audit project in Semrush to add them.
          </p>
        </>
      )}

      <WebVitals data={data} />
      <Lighthouse data={data} />
      <BacklinkTrend data={data} />
      {/* Stacked: when every crawler is allowed, robots.txt access is one quiet line, not a card beside a card. */}
      <AgentVisits data={data} periodLabel={periodLabel} />
      <BotAccess data={data} />
    </div>
  );
}

const PAGE_SIZE = 15;
const INDEX_COVERAGE_ID = 'index-coverage';
const COVERAGE_FILTER_LABEL: Record<CoverageException['kind'], string> = { broken: 'broken (404)', unknown: 'unknown to Google' };
const NO_ROWS: never[] = [];

/**
 * Coverage states that need someone get a status pill: a 404 or server error is
 * broken (error), a page Google has never heard of needs submitting (warning).
 * Every other state is plain text — Google's own choice, not an exception.
 */
type CoverageException = { kind: 'broken' | 'unknown'; label: string; variant: 'error' | 'warning' };
function coverageException(state: string | null): CoverageException | null {
  if (!state) return null;
  if (/\b404\b|not found|server error|5xx|soft 404/i.test(state)) return { kind: 'broken', label: 'Broken (404)', variant: 'error' };
  if (/unknown to google/i.test(state)) return { kind: 'unknown', label: 'Unknown to Google', variant: 'warning' };
  return null;
}

/**
 * The exceptions row under the tiles: 404s and pages Google has never heard of,
 * as status pills with what to do. Nothing when there are none.
 */
function CoverageExceptions({
  data,
  onSelect,
}: {
  data?: GtmHealthData;
  onSelect: (kind: CoverageException['kind']) => void;
}) {
  const pages = data?.indexCoverage?.pages ?? NO_ROWS;
  const broken = pages.filter((p) => coverageException(p.coverageState)?.kind === 'broken').length;
  const unknown = pages.filter((p) => coverageException(p.coverageState)?.kind === 'unknown').length;
  if (broken === 0 && unknown === 0) return null;
  return (
    <Card padding="md">
      <div className="flex flex-wrap items-center gap-2 text-body-sm text-text-secondary">
        <span className="font-medium text-text-primary">Needs action</span>
        {broken > 0 && (
          <button type="button" onClick={() => onSelect('broken')} className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" title="Show these pages in Index coverage">
            <Badge variant="error">{fmtNumber(broken)} broken (404) →</Badge>
          </button>
        )}
        {unknown > 0 && (
          <button type="button" onClick={() => onSelect('unknown')} className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" title="Show these pages in Index coverage">
            <Badge variant="warning">{fmtNumber(unknown)} unknown to Google →</Badge>
          </button>
        )}
        <span className="text-body-xs text-text-tertiary">
          {broken > 0 && 'fix or redirect the 404s'}
          {broken > 0 && unknown > 0 && '; '}
          {unknown > 0 && 'submit the unknown pages in Search Console or link to them'} · click a count to list those pages
        </span>
      </div>
    </Card>
  );
}

function IndexCoverage({
  data,
  filter,
  onClearFilter,
}: {
  data?: GtmHealthData;
  filter: CoverageException['kind'] | null;
  onClearFilter: () => void;
}) {
  const c = data?.indexCoverage;
  const all = c?.pages ?? NO_ROWS;
  const pages = useMemo(
    () => (filter ? all.filter((p) => coverageException(p.coverageState)?.kind === filter) : all),
    [all, filter],
  );
  const pager = usePagination(pages, PAGE_SIZE);
  // Only problem pages are listed, so the status dot is usually the same on every row.
  const sameStatus = isConstant(pages, (p) => p.indexed);
  return (
    <div id={INDEX_COVERAGE_ID} className="scroll-mt-4">
    <Card>
      <CardHeader
        title="Index coverage"
        description="Real index status from Search Console URL Inspection of every sitemap page (not the Pages seen tile, which counts any URL with impressions): only problematic pages are listed"
        action={
          filter ? (
            <button type="button" onClick={onClearFilter} className="text-body-xs text-accent hover:underline">
              Showing {fmtNumber(pages.length)} {COVERAGE_FILTER_LABEL[filter]} · show all {fmtNumber(all.length)}
            </button>
          ) : undefined
        }
      />
      {!c ? (
        <EmptyState compact title="No coverage data: connect GSC." />
      ) : (
        <>
          {/* Inline stats, not tiles: a card inside a card doubles every border. */}
          <div className="mb-6 grid grid-cols-3 gap-6">
            <InlineStat label="Indexed" value={fmtNumber(c.indexed)} sub={`of ${fmtNumber(c.sampled)} inspected`} />
            <InlineStat label="Not indexed" value={fmtNumber(c.notIndexed)} />
            <InlineStat label="Excluded" value={fmtNumber(c.excluded)} />
          </div>
          {all.length === 0 ? (
            <p className="text-body-sm text-text-secondary py-4 text-center">All {fmtNumber(c.sampled)} inspected pages are indexed.</p>
          ) : (
            <>
              <Table colWidths={['auto', '17rem']}>
                <THead>
                  <tr>
                    <Th>Page</Th>
                    <Th>Coverage state</Th>
                  </tr>
                </THead>
                <tbody>
                  {pager.pageItems.map((p) => (
                    <Tr key={p.url}>
                      <Td className="text-text-primary break-all" title={p.url}>
                        {!sameStatus && (
                          <>
                            <span className="text-text-tertiary">{p.indexed ? '●' : '○'}</span>{' '}
                          </>
                        )}
                        {stripDomain(p.url)}
                      </Td>
                      <Td className="text-text-secondary whitespace-normal break-words">
                        {(() => {
                          const ex = coverageException(p.coverageState);
                          if (ex) return <Badge variant={ex.variant}>{ex.label}</Badge>;
                          return p.coverageState ?? <Nil />;
                        })()}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
              <Pagination {...pager} onPage={pager.setPage} />
              {sameStatus && (
                <TableFooter>
                  {pages[0]?.indexed
                    ? `All ${fmtNumber(pages.length)} listed pages are indexed.`
                    : `None of the ${fmtNumber(pages.length)} listed pages is indexed.`}
                </TableFooter>
              )}
            </>
          )}
        </>
      )}
    </Card>
    </div>
  );
}

function SiteAudit({ data }: { data?: GtmHealthData }) {
  const a = data?.siteAudit;
  return (
    <Card>
      <CardHeader title="Site audit" description="Crawlability, redirects & hreflang (Semrush Site Audit)" />
      {!a ? (
        <EmptyState compact title="No site-audit snapshot: configure a Semrush Site Audit project." />
      ) : (
        <div className="grid grid-cols-2 gap-6">
          <InlineStat label="Health score" value={a.healthScore === null ? '—' : `${a.healthScore.toFixed(0)}`} />
          <InlineStat label="Errors" value={a.errors === null ? '—' : fmtNumber(a.errors)} />
          <InlineStat label="Crawl errors" value={a.crawlErrors === null ? '—' : fmtNumber(a.crawlErrors)} />
          <InlineStat label="Redirect issues" value={a.redirectIssues === null ? '—' : fmtNumber(a.redirectIssues)} />
          <InlineStat label="hreflang issues" value={a.hreflangIssues === null ? '—' : fmtNumber(a.hreflangIssues)} />
          <InlineStat label="Warnings" value={a.warnings === null ? '—' : fmtNumber(a.warnings)} />
        </div>
      )}
    </Card>
  );
}

/** Google's INP threshold: above 200 ms needs improvement. */
const INP_GOOD_MS = 200;

/** Google's CLS threshold: above 0.1 needs improvement; below it the value is noise worth no column. */
const CLS_GOOD = 0.1;

/** The value at least `share` of rows have, else null — said once instead of on every row. */
function dominant<T>(rows: readonly T[], pick: (row: T) => string | null, share = 0.8): string | null {
  if (rows.length < 2) return null;
  const counts = new Map<string, number>();
  for (const r of rows) {
    const v = pick(r);
    if (v !== null) counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  for (const [v, n] of counts) if (n / rows.length >= share) return v;
  return null;
}

type Device = 'mobile' | 'desktop';
const DEVICE_OPTIONS: readonly TabItem<Device>[] = [
  { id: 'mobile', label: 'Mobile' },
  { id: 'desktop', label: 'Desktop' },
];

function WebVitals({ data }: { data?: GtmHealthData }) {
  const rows = data?.webVitals;
  const all = rows ?? NO_ROWS;
  const devices = useMemo(() => new Set(all.map((r) => r.formFactor.toLowerCase())), [all]);
  const [device, setDevice] = useState<Device>('mobile');
  // Every page is measured on both devices: one device at a time instead of a Device column on every row.
  const active: Device | null = devices.size > 1 ? device : null;
  const list = useMemo(() => (active ? all.filter((r) => r.formFactor.toLowerCase() === active) : all), [all, active]);
  const sameAssessment = isConstant(list, (r) => cwvLabel(r.overall));
  // One verdict on most pages is said once; only the pages that differ carry a pill.
  const commonOverall = sameAssessment ? null : dominant(list, (r) => cwvLabel(r.overall));
  const commonCount = commonOverall ? list.filter((r) => cwvLabel(r.overall) === commonOverall).length : 0;
  // The pages whose verdict differs. When that's a page or two (1 of 24 on 12 Oct),
  // an Overall column is all blanks: name them in the footer instead.
  const overallExceptions = commonOverall ? list.filter((r) => cwvLabel(r.overall) !== commonOverall) : NO_ROWS;
  const showOverall = !sameAssessment && !isSparseColumn(list, (r) => cwvLabel(r.overall) !== commonOverall);
  const clsExceptions = list.filter((r) => r.cls !== null && r.cls >= CLS_GOOD).length;
  const pager = usePagination(list, PAGE_SIZE);
  const first = list[0];
  const notes = [
    !active && first && devices.size === 1 && `${first.formFactor.toLowerCase()} only`,
    clsExceptions === 0 && `CLS under ${CLS_GOOD} (good) on every page`,
    sameAssessment && first && (cwvLabel(first.overall) !== '—' ? `overall: ${cwvLabel(first.overall).toLowerCase()} on every page` : 'no CrUX overall assessment (not enough field traffic)'),
    commonOverall &&
      `overall: ${commonOverall === '—' ? 'no CrUX assessment' : commonOverall.toLowerCase()} on ${fmtNumber(commonCount)} of ${fmtNumber(list.length)} pages; ${
        showOverall
          ? 'only the others are marked'
          : `the ${overallExceptions.length === 1 ? 'other is' : 'others are'} ${overallExceptions
              .map((r) => `${stripDomain(r.url)} (${cwvLabel(r.overall) === '—' ? 'no assessment' : cwvLabel(r.overall).toLowerCase()})`)
              .join(', ')}`
      }`,
  ].filter(Boolean) as string[];
  return (
    <Card>
      <CardHeader
        title="Core Web Vitals"
        description="Field data (p75) for key pages, Chrome’s rolling 28 days (PageSpeed / CrUX)"
        action={active ? <SegmentedControl ariaLabel="Device" options={DEVICE_OPTIONS} value={device} onChange={setDevice} /> : undefined}
      />
      {!rows || rows.length === 0 ? (
        <EmptyState compact title="No Core Web Vitals captured yet: the daily sync collects these (optionally set PAGESPEED_API_KEY)." />
      ) : (
        <>
          <Table colWidths={['auto', '5.5rem', '5.5rem', ...(clsExceptions > 0 ? ['4.5rem'] : []), ...(showOverall ? ['7rem'] : [])]}>
            <THead>
              <tr>
                <Th>Page</Th>
                <Th align="right">LCP</Th>
                <Th align="right" title={`Above ${INP_GOOD_MS} ms is Google's "needs improvement" line`}>INP</Th>
                {clsExceptions > 0 && <Th align="right">CLS</Th>}
                {showOverall && <Th align="right">Overall</Th>}
              </tr>
            </THead>
            <tbody>
              {pager.pageItems.map((r) => (
                <Tr key={`${r.url}-${r.formFactor}`}>
                  <Td className="text-text-primary break-all" title={r.url}>{stripDomain(r.url)}</Td>
                  <Td align="right">{r.lcpMs === null ? <Nil /> : `${(r.lcpMs / 1000).toFixed(2)}s`}</Td>
                  <Td align="right">
                    {r.inpMs === null ? <Nil /> : r.inpMs > INP_GOOD_MS ? <Badge variant="warning">{r.inpMs}ms</Badge> : `${r.inpMs}ms`}
                  </Td>
                  {clsExceptions > 0 && (
                    <Td align="right">
                      {r.cls === null ? <Nil /> : r.cls >= CLS_GOOD ? <Badge variant="warning">{r.cls.toFixed(2)}</Badge> : r.cls.toFixed(2)}
                    </Td>
                  )}
                  {showOverall && (
                    <Td align="right">
                      {commonOverall && cwvLabel(r.overall) === commonOverall ? null : cwvVariant(r.overall) ? (
                        <Badge variant={cwvVariant(r.overall)!}>{cwvLabel(r.overall)}</Badge>
                      ) : (
                        cwvLabel(r.overall)
                      )}
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </Table>
          <Pagination {...pager} onPage={pager.setPage} />
          {notes.length > 0 && <TableFooter>{notes.join(' · ').replace(/^./, (c) => c.toUpperCase())}.</TableFooter>}
        </>
      )}
    </Card>
  );
}

/** The first Lighthouse opportunity as one line: "Reduce unused JavaScript (1.2 s)". */
function topFixTitle(r: NonNullable<GtmHealthData['lighthouse']>[number]): string | null {
  return r.opportunities[0]?.title ?? null;
}

function Lighthouse({ data }: { data?: GtmHealthData }) {
  const rows = data?.lighthouse;
  // A page Index coverage reports as 404 has no live page to score: an audit of it
  // (Perf 60 on /de/ressourcen/glossar/bid-management-tools, 13 Oct) is of an error page.
  const brokenPaths = useMemo(
    () =>
      new Set(
        (data?.indexCoverage?.pages ?? NO_ROWS)
          .filter((p) => coverageException(p.coverageState)?.kind === 'broken')
          .map((p) => stripDomain(p.url).replace(/\/$/, '')),
      ),
    [data?.indexCoverage],
  );
  const all = rows ?? NO_ROWS;
  const list = useMemo(() => all.filter((r) => !brokenPaths.has(stripDomain(r.url).replace(/\/$/, ''))), [all, brokenPaths]);
  const brokenAudited = all.filter((r) => brokenPaths.has(stripDomain(r.url).replace(/\/$/, '')));
  const inspected = data?.indexCoverage?.sampled ?? null;
  const pager = usePagination(list, PAGE_SIZE);
  const sameDevice = isConstant(list, (r) => r.formFactor);
  // The top fix most pages share is one site-wide task: said once above the table;
  // only pages whose top fix differs carry it, as a sub-line.
  const sharedFix = dominant(list, topFixTitle);
  const sharedCount = sharedFix ? list.filter((r) => topFixTitle(r) === sharedFix).length : 0;
  // SEO scores 100 on almost every page (144 of 145 on 12 Oct): a column of 100s. Hidden then, and said once.
  const seoFull = list.filter((r) => r.seoScore === 100).length;
  const hideSeo = list.length >= 2 && seoFull / list.length >= 0.9;
  const seoBelow = hideSeo ? list.filter((r) => r.seoScore !== null && r.seoScore !== 100) : [];
  return (
    <Card>
      <CardHeader
        title="Lighthouse audit"
        description={`Lab audit of ${fmtNumber(list.length)}${inspected ? ` of the ${fmtNumber(inspected)} inspected sitemap` : ''} pages: Performance, Accessibility, Best Practices, SEO (PageSpeed Insights, daily)`}
      />
      {!rows || rows.length === 0 ? (
        <EmptyState compact title="No Lighthouse audits yet: set the site URL on the PageSpeed integration; the daily audit crawls the sitemap." />
      ) : (
        <>
        {sharedFix && (
          <p className="mb-3 text-body-sm text-text-secondary">
            Top fix on {sharedCount === list.length ? 'every page' : `${fmtNumber(sharedCount)} of ${fmtNumber(list.length)} pages`}:{' '}
            <span className="text-text-primary">{sharedFix}</span>
          </p>
        )}
        <Table colWidths={['auto', ...(sameDevice ? [] : ['6rem']), '4.5rem', '4.5rem', '5.5rem', ...(hideSeo ? [] : ['4.5rem'])]}>
          <THead>
            <tr>
              <Th>Page</Th>
              {!sameDevice && <Th>Device</Th>}
              <Th align="right">Perf</Th>
              <Th align="right">A11y</Th>
              <Th align="right">Best Pr.</Th>
              {!hideSeo && <Th align="right">SEO</Th>}
            </tr>
          </THead>
          <tbody>
            {pager.pageItems.map((r) => {
              const fix = topFixTitle(r);
              const own = fix !== sharedFix ? r.opportunities[0] : undefined;
              return (
                <Tr key={`${r.url}-${r.formFactor}`} className="align-top">
                  <Td className="text-text-primary" title={r.url}>
                    <div className="break-words">{stripDomain(r.url)}</div>
                    {own && (
                      <div className="text-body-xs text-text-tertiary">
                        Top fix: {own.title}
                        {own.displayValue ? ` (${own.displayValue})` : ''}
                      </div>
                    )}
                  </Td>
                  {!sameDevice && <Td className="capitalize text-text-secondary">{r.formFactor.toLowerCase()}</Td>}
                  <Td align="right"><Score value={r.perfScore} /></Td>
                  <Td align="right"><Score value={r.a11yScore} /></Td>
                  <Td align="right"><Score value={r.bestPracticesScore} /></Td>
                  {!hideSeo && <Td align="right"><Score value={r.seoScore} /></Td>}
                </Tr>
              );
            })}
          </tbody>
        </Table>
        <Pagination {...pager} onPage={pager.setPage} />
        {((sameDevice || hideSeo) && list[0]) || brokenAudited.length > 0 ? (
          <TableFooter>
            {[
              brokenAudited.length > 0 &&
                `${fmtNumber(brokenAudited.length)} audited ${brokenAudited.length === 1 ? 'page is' : 'pages are'} 404 in Index coverage and left out (${brokenAudited.map((r) => stripDomain(r.url)).join(', ')}).`,
              sameDevice && list[0] && `Audited on ${list[0].formFactor.toLowerCase()}. Scores of 90 and up are good and shown plain.`,
              hideSeo &&
                `SEO scores 100 on ${fmtNumber(seoFull)} of ${fmtNumber(list.length)} pages, so that column is hidden${
                  seoBelow.length > 0
                    ? `; below 100: ${seoBelow
                        .slice(0, 5)
                        .map((r) => `${stripDomain(r.url)} (${r.seoScore})`)
                        .join(', ')}${seoBelow.length > 5 ? ` and ${seoBelow.length - 5} more` : ''}`
                    : ''
                }.`,
            ]
              .filter(Boolean)
              .join(' ')}
          </TableFooter>
        ) : null}
        </>
      )}
    </Card>
  );
}

function AgentVisits({ data, periodLabel }: { data?: GtmHealthData; periodLabel?: string }) {
  const rows = data?.agentVisits;
  const pager = usePagination(rows ?? NO_ROWS, PAGE_SIZE);
  return (
    <Card>
      <CardHeader
        title="AI-bot crawlability"
        description={`${periodLabel ? `${periodLabel} · ` : ''}${rows && rows.length > 0 ? `${fmtNumber(rows.length)} AI bots visited` : 'Which AI crawlers reach our content'}, by hit count (Peec)`}
      />
      {!rows || rows.length === 0 ? (
        <EmptyState compact title="No AI-bot visits recorded for this period." />
      ) : (
        <>
        <Table>
          <THead>
            <tr>
              <Th>Bot</Th>
              <Th align="right">Visits</Th>
            </tr>
          </THead>
          <tbody>
            {pager.pageItems.map((r) => (
              <Tr key={r.botId ?? r.botName ?? 'unknown'}>
                <Td className="text-text-primary">{r.botName ?? r.botId ?? 'Unknown bot'}</Td>
                <Td align="right">{fmtNumber(r.visits)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
        <Pagination {...pager} onPage={pager.setPage} />
        </>
      )}
    </Card>
  );
}

function BotAccess({ data }: { data?: GtmHealthData }) {
  const rows = data?.botAccess;
  const blocked = rows?.filter((r) => !r.allowed) ?? [];
  // Healthy state shows nothing: when every crawler is allowed, one quiet line replaces the card.
  if (rows && rows.length > 0 && blocked.length === 0) {
    return (
      <p className="px-1 text-body-xs text-text-tertiary">
        robots.txt: all {rows.length} AI crawlers it is checked for by name (GPTBot, ClaudeBot, PerplexityBot…) are allowed
        (live check). The visits above list every AI bot Peec saw, a wider set.
      </p>
    );
  }
  return (
    <Card>
      <CardHeader title="AI-bot robots.txt access" description="Can each AI crawler reach the site root? (live robots.txt check)" />
      {!rows || rows.length === 0 ? (
        <EmptyState compact title="robots.txt check unavailable." />
      ) : (
        <>
          <p className="mb-3 flex items-center gap-2 text-body-sm text-text-secondary">
            <Badge variant="error">{blocked.length} blocked</Badge>
            of {rows.length} AI crawler{rows.length > 1 ? 's' : ''} in robots.txt.
          </p>
          <Table>
            <THead>
              <tr>
                <Th>Bot</Th>
                <Th>Vendor</Th>
                <Th align="right">Access</Th>
              </tr>
            </THead>
            <tbody>
              {rows.map((r) => (
                <Tr key={r.bot}>
                  <Td className="text-text-primary">{r.bot}</Td>
                  <Td className="text-text-secondary">{r.vendor}</Td>
                  <Td align="right" className="text-text-tertiary" title={r.matchedRule ?? ''}>
                    {r.allowed ? 'Allowed' : <Badge variant="error">Blocked</Badge>}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </>
      )}
    </Card>
  );
}

/**
 * CrUX's overall verdict. PageSpeed's field data says FAST / AVERAGE / SLOW;
 * older payloads said GOOD / NEEDS_IMPROVEMENT / POOR. Until 9 Oct only the
 * latter were read, so every page showed "—" and the footer claimed no CrUX
 * assessment.
 */
function cwvLabel(overall: string | null): string {
  if (overall === 'GOOD' || overall === 'FAST') return 'Fast';
  if (overall === 'NEEDS_IMPROVEMENT' || overall === 'AVERAGE') return 'Average';
  if (overall === 'POOR' || overall === 'SLOW') return 'Slow';
  return '—';
}

/** Healthy shows plain; only average/slow get a status pill. */
function cwvVariant(overall: string | null): 'warning' | 'error' | null {
  if (overall === 'NEEDS_IMPROVEMENT' || overall === 'AVERAGE') return 'warning';
  if (overall === 'POOR' || overall === 'SLOW') return 'error';
  return null;
}

/**
 * Lighthouse score bands: 0–49 poor, 50–89 needs work, 90–100 good. Good is plain
 * text; only the two exception bands get a status pill (until 9 Oct every score
 * was coloured text, green included).
 */
function Score({ value }: { value: number | null }) {
  if (value === null) return <Nil />;
  if (value >= 90) return <>{value}</>;
  return <Badge variant={value < 50 ? 'error' : 'warning'}>{value}</Badge>;
}

function stripDomain(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname + u.search;
  } catch {
    return url;
  }
}

/** Short tile labels so they fit a sixth of the row; the full label is the tooltip. */
const SHORT_LABEL: Record<string, string> = {
  pages_with_impressions: 'Pages seen',
  avg_position: 'GSC avg pos.',
  referring_domains: 'Ref. domains',
  authority_score: 'Authority',
};

/** One row on wide screens, whatever the number of tiles (static classes for Tailwind). */
const SCORECARD_COLS: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
};

/** "GSC" → "Search Console", so the two average positions on these tabs can't be confused. */
const SOURCE_NAMES: Record<string, string> = { GSC: 'Search Console' };

/**
 * What period a tile really covers: Semrush counts are a snapshot on one day
 * ("as of 8 Oct 2026"), the tracking check counts the last 24 hours; only the
 * Search Console tiles follow the period chip. Until 9 Oct every tile said the
 * chip's period.
 */
function scorecardPeriod(s: GtmHealthData['scorecards'][number], periodLabel?: string): string | undefined {
  if (s.asOf) return `as of ${formatDayMonthYear(s.asOf)}`;
  return periodLabel;
}

function Scorecards({ data, periodLabel }: { data?: GtmHealthData; periodLabel?: string }) {
  // The tracking-events count is the website pipeline's pulse, shown on Marketing → Website, not an SEO metric.
  const cards = (data?.scorecards ?? []).filter((s) => s.key !== 'tracking_events_24h');
  if (!data || cards.length === 0) {
    return (
      <Card>
        <EmptyState compact title="No health metrics yet: connect GSC and Semrush." />
      </Card>
    );
  }
  return (
    <div className={clsx('grid grid-cols-2 gap-4 md:grid-cols-3', SCORECARD_COLS[Math.min(cards.length, 6)])}>
      {cards.map((s) => (
        <StatTile
          key={s.key}
          label={SHORT_LABEL[s.key] ?? s.label}
          sub={[scorecardPeriod(s, periodLabel), SOURCE_NAMES[s.source] ?? s.source].filter(Boolean).join(' · ')}
          tooltip={
            s.key === 'pages_with_impressions'
              ? `${s.label}. Counts every URL that showed in Google in the period, sitemap or not, so it is higher than the sitemap pages inspected for indexing below.`
              : s.label
          }
          value={s.value === null ? '—' : s.unit === 'number' ? s.value.toFixed(1) : fmtNumber(s.value)}
        />
      ))}
    </div>
  );
}

function BacklinkTrend({ data }: { data?: GtmHealthData }) {
  const points = data?.backlinkTrend;
  if (!points || points.length === 0) {
    return (
      <Card>
        <CardHeader title="Referring domains & backlinks" description="Authority growth (Semrush)" />
        <EmptyState compact title="No Semrush backlink snapshot in this period." />
      </Card>
    );
  }
  const newTotal = points.reduce((acc, p) => acc + (p.newBacklinks ?? 0), 0);
  const lostTotal = points.reduce((acc, p) => acc + (p.lostBacklinks ?? 0), 0);
  const hasDeltas = points.some((p) => p.newBacklinks !== null || p.lostBacklinks !== null);
  return (
    <Card>
      <CardHeader
        title="Referring domains & backlinks"
        description={`Daily Semrush snapshots inside the period, ${formatDayMonthYear(points[0]!.date)} to ${formatDayMonthYear(points[points.length - 1]!.date)}`}
      />
      {hasDeltas && (
        <p className="text-body-sm mb-2">
          <span className="text-text-primary tabular-nums">+{fmtNumber(newTotal)} new / −{fmtNumber(lostTotal)} lost</span>
          <span className="text-text-tertiary"> backlinks in this period (derived from daily snapshots)</span>
        </p>
      )}
      {/* Two small charts, one axis each: the two counts differ by an order of
          magnitude, and a shared chart needed two y-axes with no way to tell them apart. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <BacklinkMiniChart points={points} dataKey="referringDomains" label="Referring domains" />
        <BacklinkMiniChart points={points} dataKey="backlinks" label="Backlinks" />
      </div>
    </Card>
  );
}

function BacklinkMiniChart({
  points,
  dataKey,
  label,
}: {
  points: NonNullable<GtmHealthData['backlinkTrend']>;
  dataKey: 'referringDomains' | 'backlinks';
  label: string;
}) {
  const latest = [...points].reverse().find((p) => p[dataKey] !== null)?.[dataKey] ?? null;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-body-xs text-text-tertiary">{label}</span>
        <span className="text-body-sm font-medium text-text-primary tabular-nums">
          {latest === null ? '—' : fmtNumber(latest)}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={CHART_HEIGHT.small}>
        <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid {...CHART_GRID} />
          <XAxis dataKey="date" {...CHART_AXIS} minTickGap={32} tickFormatter={(d) => formatDayMonth(String(d))} />
          <YAxis {...CHART_AXIS} width={44} domain={['auto', 'auto']} tickFormatter={(v) => fmtNumber(Number(v))} />
          <Tooltip {...CHART_TOOLTIP} labelFormatter={(d) => formatDayMonthYear(String(d))} />
          <Line type="linear" dataKey={dataKey} name={label} stroke={CHART_SERIES[0]} dot={false} strokeWidth={2} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
